import { Component, inject, OnInit } from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { TuiDay } from '@taiga-ui/cdk';
import {
  TuiAppearance,
  TuiButton,
  TuiDataList,
  tuiDateFormatProvider,
  TuiDialogContext,
  TuiError,
  TuiGroup,
  TuiLabel,
  TuiTextfield,
} from '@taiga-ui/core';
import { injectContext } from '@taiga-ui/polymorpheus';
import { Organisme } from '../../models/organisme';
import { Patient } from '../../models/patient';
import { PrescriptionFormDialogData } from '../../models/prescription-form-dialog-data';
import { PrescriptionForm } from '../../models/prescriptionForm/prescriptionForm';
import { User } from '../../models/user';
import { AuthService } from '../../services/auth.service';
import { PrescriptionFormService } from '../../services/prescription-form.service';
import { AsyncPipe } from '@angular/common';
import {
  TuiFieldErrorPipe,
  TuiBlock,
  TuiRadio,
  TuiSwitch,
  TuiFilterByInputPipe,
  TuiDataListWrapper,
  TuiAvatar,
  tuiItemsHandlersProvider,
} from '@taiga-ui/kit';
import { TuiCardLarge } from '@taiga-ui/layout';
import {
  TuiInputModule,
  TuiTextfieldControllerModule,
  TuiInputDateModule,
  TuiTextareaModule,
  TuiComboBoxModule,
} from '@taiga-ui/legacy';
import { AerosolOrdonnanceInformationsForm } from '../../models/prescriptionForm/aerosolOrdonnanceInformationsForm';
import { Prescription } from '../../models/prescription';

@Component({
  selector: 'app-prescription-form-aerosols',
  standalone: true,
  imports: [
    TuiCardLarge,
    TuiAppearance,
    ReactiveFormsModule,
    TuiLabel,
    TuiError,
    TuiFieldErrorPipe,
    AsyncPipe,
    TuiInputModule,
    TuiTextfield,
    TuiTextfieldControllerModule,
    TuiBlock,
    TuiGroup,
    TuiRadio,
    TuiSwitch,
    TuiInputDateModule,
    TuiTextareaModule,
    TuiComboBoxModule,
    TuiFilterByInputPipe,
    TuiDataList,
    TuiDataListWrapper,
    TuiButton,
    TuiAvatar,
  ],
  templateUrl: './prescription-form-aerosols.component.html',
  styleUrl: './prescription-form-aerosols.component.scss',
  providers: [
    tuiDateFormatProvider({ mode: 'DMY', separator: '/' }),
    tuiItemsHandlersProvider({
      stringify: (item: User | string) =>
        item instanceof User ? `${item.nom} ${item.prenom}` : item,
    }),
  ],
})
export class PrescriptionFormAerosolsComponent implements OnInit {
  public readonly context =
    injectContext<TuiDialogContext<void, PrescriptionFormDialogData | void>>();

  private prescriptionFormService = inject(PrescriptionFormService);
  protected authService = inject(AuthService);

  public etatPush: undefined | boolean | 'loading';

  public form!: FormGroup;

  public patient!: Patient | undefined;
  public codeDActivite!: string;
  public userList!: User[];
  public organisme!: Organisme | null;
  public reedition!: boolean;
  public prescription!: Prescription | undefined;
  public prescriptionFormPrecedente!: PrescriptionForm | undefined;

  ngOnInit(): void {
    this.patient = this.context.data!.patient;
    this.prescription = this.context.data!.prescription;
    this.codeDActivite = this.context.data!.codeDActivite;
    this.prescriptionFormPrecedente =
      this.context.data!.prescriptionFormPrecedente;
    this.userList = this.context.data!.userList;
    this.organisme = this.context.data!.organisme;
    this.reedition = this.context.data!.reedition;

    let user = this.authService.getUser();

    this.form = new FormGroup({
      typeSlug: new FormControl('aerosol_ordonnance', [
        Validators.required,
        Validators.pattern('^aerosol_ordonnance$'),
      ]),
      medecin: new FormControl(
        (() => {
          if (
            this.userList.find(
              (test) => test.id == this.authService.getUser()?.id,
            )
          ) {
            return user;
          } else {
            return null;
          }
        })(),
        [Validators.required],
      ), // different
      userId: new FormControl(user?.id, [
        Validators.required,
        Validators.pattern('^[0-9]+$'),
      ]),
      nir: new FormControl(this.patient?.nir ?? null, [
        Validators.required,
        Validators.pattern(
          '^[12][0-9]{2}(0[1-9]|1[0-2])(2[ABab]|[0-9]{2})[0-9]{8}$',
        ),
      ]),
      codeDActivite: new FormControl(this.codeDActivite, [Validators.required]),
      dateDePrescription: new FormControl(
        TuiDay.fromLocalNativeDate(this.prescription?.dateDebut ?? new Date()),
        [Validators.required],
      ),
      nom: new FormControl(this.patient?.nom ?? null, [Validators.required]),
      prenom: new FormControl(this.patient?.prenom ?? null, [
        Validators.required,
      ]),
      sexe: new FormControl(
        this.patient?.sexe == 'Homme'
          ? 'homme'
          : this.patient?.sexe == 'Femme'
            ? 'femme'
            : null,
        [Validators.required, Validators.pattern('^(homme|femme)$')],
      ),
      dateDeNaissance: new FormControl(
        this.patient?.dateDeNaissance
          ? TuiDay.fromLocalNativeDate(this.patient.dateDeNaissance)
          : null,
        [Validators.required],
      ),
      adresseLigne1: new FormControl(this.patient?.adresse?.ligne1 ?? null, [
        Validators.required,
      ]),
      adresseLigne2: new FormControl(
        this.patient?.adresse?.ligne2 && this.patient?.adresse?.ligne3
          ? this.patient?.adresse?.ligne2 + ', ' + this.patient?.adresse?.ligne3
          : (this.patient?.adresse?.ligne2 ??
              this.patient?.adresse?.ligne3 ??
              null),
      ),
      adresseCodePostal: new FormControl(
        this.patient?.adresse?.codePostal ?? null,
        [Validators.required],
      ),
      adresseVille: new FormControl(this.patient?.adresse?.ville ?? null, [
        Validators.required,
      ]),
      telephone: new FormControl(
        this.patient?.mobile ?? this.patient?.telephone ?? null,
        [Validators.required],
      ),
      aSigner: new FormControl(
        (() => {
          if (
            this.patient?.canSign ||
            (!this.patient && this.authService.getUser()?.rpps)
          ) {
            return true;
          } else {
            return false;
          }
        })(),
      ),

      // Informations Formulaire PPC DAP
      nombreNebuliseurParMois: new FormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]+$'),
      ]),
      prescriptionDuree: new FormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]+$'),
      ]),
    });
  }

  selectMe() {
    this.form.get('medecin')?.setValue(this.authService.getUser());
  }

  protected readonly matcherString = (user: User, search: string): boolean =>
    (`${user.prenom}`
      .split(' ')
      .pop()
      ?.toLowerCase()
      .startsWith(search.toLowerCase()) ||
      `${user.nom}`
        .split(' ')
        .pop()
        ?.toLowerCase()
        .startsWith(search.toLowerCase())) ??
    false;

  initEtatPush(): void {
    this.etatPush = undefined;
  }

  async onSubmit() {
    if (this.prescriptionFormPrecedente && this.reedition) {
      this.prescriptionFormService
        .delete(this.prescriptionFormPrecedente)
        .subscribe({
          next: async (data) => {
            await this.post();
          },
          error: (error) => {
            console.log(error);
          },
        });
    } else {
      await this.post();
    }
  }

  async post() {
    if (this.patient) {
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        if (
          control?.errors?.['required'] &&
          (control.value === null || control.value === '')
        ) {
          control.setValue('Ø');
          control.updateValueAndValidity();
        }
      });
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      console.error('Formulaire invalide', this.form.value);
      return;
    }
    this.etatPush = 'loading';

    var prescriptionForm: PrescriptionForm = new PrescriptionForm(
      undefined,
      this.form.value.typeSlug,
      undefined,
      this.form.value.medecin.id,
      undefined,
      this.form.value.userId,
      undefined,
      this.form.value.nir,
      this.form.value.codeDActivite,
      this.form.value.dateDePrescription.toUtcNativeDate(),
      this.form.value.nom,
      this.form.value.prenom,
      this.form.value.sexe,
      this.form.value.dateDeNaissance.toUtcNativeDate(),
      this.form.value.adresseLigne1,
      this.form.value.adresseLigne2,
      this.form.value.adresseCodePostal,
      this.form.value.adresseVille,
      this.form.value.telephone,
      this.form.value.aSigner ?? false,
      this.form.value.aSigner ?? false,
      null,
      undefined,
      this.prescription?.id,
      undefined,
      new AerosolOrdonnanceInformationsForm(
        this.form.value.nombreNebuliseurParMois,
        this.form.value.prescriptionDuree,
      ),
    );

    this.prescriptionFormService.push(prescriptionForm).subscribe({
      next: (data) => {
        this.etatPush = true;
      },
      error: (error) => {
        this.etatPush = false;
      },
    });
  }

  canSign() {
    return this.userList.find(
      (test) => test.id == this.authService.getUser()?.id,
    );
  }
}
